/**
 * PD-2 — the pebble timeline.
 *
 * One screen answering "what did the user say, what did the reasoner decide,
 * what did the interviewer do", over PD-1's `GET /admin/conversation/:pebbleId`.
 *
 * ## Deep-linkable by pebble id, and that is a design constraint not a nicety
 *
 * The acceptance asks for it, and every other PD package links INTO this page:
 * PD-4 from a match-events row, PD-6 from a flow issue, PD-3 from the reconcile
 * trace. So the pebble id lives in the PATH (`/conversation/:pebbleId`) and the
 * view options live in the QUERY STRING — never in component state alone. A
 * pasted URL reproduces the exact screen, including a widened transcript window,
 * and the back button walks the ids an operator visited.
 *
 * `/conversation` with no id renders the id form, so the nav entry has somewhere
 * to go.
 *
 * ## The wide window is opt-in
 *
 * PD-1's defaults (1000 archived turns, 500 legacy rows, 200 verdicts, 200 live
 * nodes, 100 events) cover essentially every real pebble. `?full=1` raises each
 * to the route's own ceiling. It is a button rather than the default because
 * those ceilings are five to ten times the payload, and a 200-turn pebble is the
 * shape the acceptance measures — not the shape that needs the ceiling.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getConversationTrace } from '../api/client';
import type { ConversationTrace, ConversationTraceOptions } from '../api/types';
import ConversationTimeline from '../components/conversation/ConversationTimeline';
import TraceHeader from '../components/conversation/TraceHeader';

/** The route's own maxima (`LIMITS` in `routes/admin-conversation.ts`). Asking
 *  for more is a 400, so these are pinned to that table and not invented here. */
const FULL: ConversationTraceOptions = {
  transcript: 5000,
  verdicts: 1000,
  chats: 2000,
  messages: 200,
  events: 100,
};

export default function ConversationPage() {
  const { pebbleId } = useParams<{ pebbleId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const full = params.get('full') === '1';
  const expandAll = params.get('expand') === '1';

  const [trace, setTrace] = useState<ConversationTrace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftId, setDraftId] = useState(pebbleId ?? '');

  useEffect(() => {
    setDraftId(pebbleId ?? '');
  }, [pebbleId]);

  /** Every request this component starts gets a sequence number, and only the
   *  LATEST one may write. A per-call `cancelled` flag was not enough: the
   *  Reload button dropped the closure that owned it, so a slow reload of A
   *  could still land — and repaint A's trace — after the operator had already
   *  navigated to B. On a screen whose whole value is "what is drawn is what
   *  happened", a stale write is worse than a spinner. */
  const reqSeq = useRef(0);

  const load = useCallback((id: string, wide: boolean) => {
    const seq = (reqSeq.current += 1);
    const latest = () => reqSeq.current === seq;
    setLoading(true);
    setError(null);
    getConversationTrace(id, wide ? FULL : {})
      .then((data) => {
        if (latest()) setTrace(data);
      })
      .catch((e) => {
        if (latest()) {
          setTrace(null);
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (latest()) setLoading(false);
      });
  }, []);

  // Changing pebble clears the screen FIRST. Rendering A's header (A's
  // pebble_id, A's plane, A's timeline) through B's fetch is the one thing this
  // page must never do: it is a complete, confident trace of the wrong pebble.
  useEffect(() => {
    setTrace(null);
    setError(null);
  }, [pebbleId]);

  useEffect(() => {
    if (!pebbleId) {
      reqSeq.current += 1;
      setTrace(null);
      setLoading(false);
      return;
    }
    load(pebbleId, full);
    // Invalidates whatever is in flight — the effect's own request and any the
    // Reload button started — on unmount and on every id/window change.
    return () => {
      reqSeq.current += 1;
    };
  }, [pebbleId, full, load]);

  function setFlag(name: string, on: boolean) {
    const next = new URLSearchParams(params);
    if (on) next.set(name, '1');
    else next.delete(name);
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-4">
      <form
        className="card flex items-end gap-3 flex-wrap"
        onSubmit={(e) => {
          e.preventDefault();
          const id = draftId.trim();
          // The URL is the state: navigating is what loads the trace, which is
          // also what makes the result a link the operator can paste.
          if (id) navigate(`/conversation/${encodeURIComponent(id)}${params.toString() ? `?${params}` : ''}`);
        }}
      >
        <div className="flex-1 min-w-[18rem]">
          <label htmlFor="pebble-id" className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Pebble id
          </label>
          <input
            id="pebble-id"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
            placeholder="pebble uuid"
            value={draftId}
            onChange={(e) => setDraftId(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary">
          Open
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!pebbleId || loading}
          onClick={() => pebbleId && load(pebbleId, full)}
        >
          {loading ? 'Loading…' : 'Reload'}
        </button>
        <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={full} onChange={(e) => setFlag('full', e.target.checked)} />
          full window
        </label>
        <label className="flex items-center gap-1 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={expandAll} onChange={(e) => setFlag('expand', e.target.checked)} />
          expand passes
        </label>
      </form>

      {!pebbleId && (
        <div className="card text-sm text-gray-500">
          Enter a pebble id, or open this page from a match, story, flow issue or match-event row.
        </div>
      )}

      {error && (
        <div className="card text-sm text-red-800 bg-red-50 border border-red-200" role="alert">
          {error}
        </div>
      )}

      {loading && !trace && <div className="card text-sm text-gray-500">Loading trace…</div>}

      {trace && (
        <>
          <TraceHeader trace={trace} />
          <ConversationTimeline trace={trace} expandAll={expandAll} />
        </>
      )}
    </div>
  );
}
